import React, { useState, useRef, useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import Field from './Field';
import FieldPalette from './FieldPalette';
import {
  screenToRelative,
  calculateOptimalContainerSize,
  getPdfDimensions,
} from '../utils/coordinateConversion';
import '../styles/PDFEditor.css';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const PDFEditor = ({ pdfFile = null }) => {
  const [fields, setFields] = useState([]);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [pdfPages, setPdfPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [scale, setScale] = useState(1);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [pdfDimensions, setPdfDimensions] = useState(getPdfDimensions('A4'));
  const [uploadedPdfFile, setUploadedPdfFile] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const containerRef = useRef(null);
  const canvasRef = useRef(null);

  // Load sample PDF or user-provided PDF
  useEffect(() => {
    const loadPdf = async () => {
      try {
        let arrayBuffer;
        
        if (pdfFile) {
          arrayBuffer = await pdfFile.arrayBuffer();
        } else {
          // Load a sample PDF (we'll create one for demo)
          const response = await fetch('/sample.pdf');
          arrayBuffer = await response.arrayBuffer();
        }

        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const pages = [];

        for (let i = 0; i < pdf.numPages; i++) {
          pages.push(await pdf.getPage(i + 1));
        }

        setPdfPages(pages);
        
        // Get dimensions from first page
        const firstPage = pages[0];
        const viewport = firstPage.getViewport({ scale: 1 });
        setPdfDimensions({ width: viewport.width, height: viewport.height });
      } catch (error) {
        console.error('Error loading PDF:', error);
      }
    };

    loadPdf();
  }, [pdfFile]);

  // Render PDF page on canvas
  useEffect(() => {
    const renderPage = async () => {
      if (pdfPages.length === 0 || !canvasRef.current) return;

      const page = pdfPages[currentPage];
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;
    };

    renderPage();
  }, [pdfPages, currentPage, scale]);

  // Calculate and update container size for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;

      const maxWidth = containerRef.current.clientWidth;
      const maxHeight = window.innerHeight - 200;

      const size = calculateOptimalContainerSize(
        pdfDimensions.width,
        pdfDimensions.height,
        maxWidth,
        maxHeight,
        scale
      );

      setContainerSize(size);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [pdfDimensions, scale]);

  // Handle drag over container
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle field drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const fieldType = e.dataTransfer.getData('fieldType');
    if (!fieldType) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Convert screen pixels to relative units
    const relPos = screenToRelative(
      x,
      y,
      containerSize.width,
      containerSize.height
    );

    // Create new field with default size
    const newField = {
      id: Date.now().toString(),
      type: fieldType,
      position: relPos,
      size: { width: 0.15, height: 0.08 }, // 15% width, 8% height (relative units)
      label: `${fieldType} Field`,
      required: false,
    };

    setFields([...fields, newField]);
    setSelectedFieldId(newField.id);
  };

  // Handle field position update
  const updateFieldPosition = (fieldId, newPosition) => {
    setFields(
      fields.map((f) =>
        f.id === fieldId ? { ...f, position: newPosition } : f
      )
    );
  };

  // Handle field size update
  const updateFieldSize = (fieldId, newSize) => {
    setFields(
      fields.map((f) => (f.id === fieldId ? { ...f, size: newSize } : f))
    );
  };

  // Handle field deletion
  const deleteField = (fieldId) => {
    setFields(fields.filter((f) => f.id !== fieldId));
    if (selectedFieldId === fieldId) setSelectedFieldId(null);
  };

  // Handle field value update
  const updateFieldValue = (fieldId, updates) => {
    setFields(
      fields.map((f) =>
        f.id === fieldId ? { ...f, ...updates } : f
      )
    );
  };

  // Handle PDF file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pages = [];

      for (let i = 0; i < pdf.numPages; i++) {
        pages.push(await pdf.getPage(i + 1));
      }

      setPdfPages(pages);
      setCurrentPage(0);
      setFields([]);
      setSelectedFieldId(null);
      setUploadedPdfFile(file); // Store the file for later download

      // Get dimensions from first page
      const firstPage = pages[0];
      const viewport = firstPage.getViewport({ scale: 1 });
      setPdfDimensions({
        width: viewport.width,
        height: viewport.height,
      });
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Failed to load PDF. Please try another file.');
    }
  };

  // Download PDF with fields embedded
  const downloadPdf = async () => {
    if (!uploadedPdfFile || fields.length === 0) {
      alert('Please upload a PDF and add at least one field');
      return;
    }

    setIsDownloading(true);
    try {
      const formData = new FormData();
      formData.append('pdfFile', uploadedPdfFile);
      
      // Filter and prepare fields - exclude binary data like signatures and images
      const fieldsToSend = fields.map((f) => {
        // For signature and image fields, we don't send the actual data
        // Just send metadata about the field
        if (f.type === 'signature' || f.type === 'image') {
          return {
            type: f.type,
            position: f.position,
            size: f.size,
            label: f.label,
            value: f.value ? 'FILLED' : '', // Just mark if filled, don't send data
          };
        }
        
        // For other fields, send the value
        return {
          type: f.type,
          position: f.position,
          size: f.size,
          label: f.label,
          value: f.value || '',
        };
      });
      
      formData.append('fields', JSON.stringify(fieldsToSend));
      formData.append('signerName', 'Document Signer');
      formData.append('signerEmail', 'signer@example.com');

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      console.log('Sending download request to', apiUrl + '/api/sign-pdf');
      console.log('Fields to send:', fieldsToSend);
      console.log('PDF file:', uploadedPdfFile.name, uploadedPdfFile.size, 'bytes');

      // Create abort controller for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.error('Request timeout - taking too long');
      }, 60000); // 60 second timeout

      const response = await fetch(apiUrl + '/api/sign-pdf', {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      }).catch((err) => {
        clearTimeout(timeoutId);
        console.error('Fetch failed:', err.message);
        throw new Error(`Network error: ${err.message}`);
      });

      clearTimeout(timeoutId);
      console.log('Response received:', response.status, response.statusText);
      console.log('Response headers:', {
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length'),
        contentDisposition: response.headers.get('content-disposition'),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Backend error response:', errorText);
        throw new Error(`Failed to process PDF: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const blob = await response.blob();
      console.log('PDF blob received:', blob.size, 'bytes', 'type:', blob.type);
      
      if (blob.size === 0) {
        throw new Error('Received empty PDF - backend processing failed');
      }

      // Verify it's a valid PDF
      if (!blob.type.includes('pdf') && !response.headers.get('content-type')?.includes('pdf')) {
        console.warn('Content-Type mismatch - expected PDF but got:', blob.type);
      }

      const url = URL.createObjectURL(blob);
      console.log('Created object URL:', url);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `bolosign-${Date.now()}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      console.log('Triggering download of:', link.download);
      link.click();
      
      // Clean up after a short delay to allow download to start
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        console.log('Cleaned up download resources');
      }, 100);

      alert(`PDF downloaded successfully! Check your Downloads folder.`);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      console.error('Error stack:', error.stack);
      alert(`Error downloading PDF: ${error.message}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const exportFields = () => {
    const exportData = {
      timestamp: new Date().toISOString(),
      pdfDimensions,
      totalFields: fields.length,
      fields: fields.map((f) => ({
        id: f.id,
        type: f.type,
        position: f.position,
        size: f.size,
        label: f.label,
        value: f.value || null,
        required: f.required,
        groupName: f.groupName,
      })),
    };
    
    // Log to console for debugging
    console.log('Exported Fields:', exportData);
    
    // Create and download JSON file
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bolosign-fields-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    alert(`Exported ${fields.length} fields!\nFile: bolosign-fields-${Date.now()}.json`);
    
    return exportData;
  };

  return (
    <div className="pdf-editor">
      <div className="editor-header">
        <h1>BoloSign - PDF Field Editor</h1>
        <div className="controls">
          <input
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
          />
          <button onClick={() => setScale(Math.max(0.5, scale - 0.1))}>
            Zoom Out
          </button>
          <span>{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale(Math.min(2, scale + 0.1))}>
            Zoom In
          </button>
          <button 
            onClick={() => setPreviewMode(!previewMode)}
            style={{
              backgroundColor: previewMode ? '#10B981' : '#f0f0f0',
              color: previewMode ? 'white' : '#333',
              fontWeight: 'bold',
              padding: '8px 12px',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            {previewMode ? 'Preview ON' : 'Edit Mode'}
          </button>
          {pdfPages.length > 1 && (
            <>
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
              >
                ← Previous
              </button>
              <span>
                Page {currentPage + 1} of {pdfPages.length}
              </span>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(pdfPages.length - 1, currentPage + 1))
                }
                disabled={currentPage === pdfPages.length - 1}
              >
                Next →
              </button>
            </>
          )}
        </div>
      </div>

      <div className="editor-content" style={{ display: 'flex', gap: '10px' }}>
        <FieldPalette />

        <div
          className="pdf-container"
          ref={containerRef}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{
            width: `${containerSize.width}px`,
            height: `${containerSize.height}px`,
          }}
        >
          <canvas ref={canvasRef} className="pdf-canvas" />

          {/* Render all fields */}
          {fields.map((field) => (
            <Field
              key={field.id}
              field={field}
              isSelected={selectedFieldId === field.id}
              containerSize={containerSize}
              pdfDimensions={pdfDimensions}
              onSelect={() => setSelectedFieldId(field.id)}
              onPositionUpdate={(newPos) =>
                updateFieldPosition(field.id, newPos)
              }
              onSizeUpdate={(newSize) => updateFieldSize(field.id, newSize)}
              onDelete={() => deleteField(field.id)}
              onFieldUpdate={updateFieldValue}
              hideBox={previewMode}
            />
          ))}
        </div>

        {/* Fields Summary Panel */}
        <div
          style={{
            width: '250px',
            backgroundColor: '#f5f5f5',
            borderRadius: '4px',
            padding: '12px',
            overflowY: 'auto',
            maxHeight: `${containerSize.height}px`,
            borderLeft: '2px solid #ddd',
          }}
        >
          <h3 style={{ marginTop: 0, fontSize: '14px', fontWeight: 'bold' }}>
            Fields Summary
          </h3>
          {fields.length === 0 ? (
            <p style={{ fontSize: '12px', color: '#999' }}>
              No fields placed yet. Drag fields from the palette onto the PDF.
            </p>
          ) : (
            <div>
              <div style={{ marginBottom: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                Total: {fields.length} field{fields.length !== 1 ? 's' : ''}
              </div>
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  onClick={() => setSelectedFieldId(field.id)}
                  style={{
                    padding: '8px',
                    marginBottom: '6px',
                    backgroundColor: selectedFieldId === field.id ? '#e3f2fd' : 'white',
                    borderLeft: `3px solid ${['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6'][index % 6]}`,
                    borderRadius: '2px',
                    cursor: 'pointer',
                    fontSize: '11px',
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                    {index + 1}. {field.type.toUpperCase()}
                  </div>
                  <div style={{ color: '#666', marginBottom: '2px' }}>
                    Label: {field.label || 'N/A'}
                  </div>
                  <div style={{ color: '#666', fontSize: '10px' }}>
                    Pos: ({(field.position.x * 100).toFixed(1)}%, {(field.position.y * 100).toFixed(1)}%)
                  </div>
                  {field.value && (
                    <div style={{ color: '#10B981', fontSize: '10px', marginTop: '2px' }}>
                      ✓ Has value
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="editor-footer">
        <button 
          className="export-btn" 
          onClick={() => downloadPdf()}
          disabled={fields.length === 0 || isDownloading}
          style={{
            backgroundColor: fields.length === 0 || isDownloading ? '#ccc' : '#3B82F6',
            color: 'white',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '4px',
            cursor: fields.length === 0 || isDownloading ? 'not-allowed' : 'pointer',
          }}
        >
          {isDownloading ? 'Processing...' : `Download PDF (${fields.length})`}
        </button>
        <span style={{ marginLeft: '20px', fontSize: '14px' }}>
          {pdfPages.length > 0 ? `PDF Loaded - ${fields.length} fields placed` : 'Upload PDF to start'}
        </span>
      </div>
    </div>
  );
};

export default PDFEditor;
